<script>
	import SocialIcons from '$lib/components/common/SocialIcons.svelte';
	import { CONFIG, getIssnUrl } from '$lib/config/index.js';

	const year = new Date().getFullYear();

	const browse = [
		{ href: `/issue/${CONFIG.conference.currentIssueId}`, label: 'Current Issue' },
		{ href: '/issues', label: 'All Issues' },
		{ href: '/articles', label: 'Articles' },
		{ href: '/search', label: 'Search' }
	];

	const machineAccess = [
		// First: the human-readable explanation of everything below it.
		{ href: '/for-machines', label: 'For machines' },
		{ href: '/oai?verb=Identify', label: 'OAI-PMH' },
		{ href: '/.well-known/resourcesync', label: 'ResourceSync' },
		{ href: '/llms.txt', label: 'LLM Context' },
		{ href: '/sitemap.xml', label: 'Sitemap' }
	];

	const dcmi = [
		{ href: 'https://www.dublincore.org/about/', label: 'About DCMI' },
		{ href: 'https://www.dublincore.org/conferences/', label: 'Conferences' },
		{
			href: 'https://www.dublincore.org/specifications/dublin-core/dcmi-terms/',
			label: 'DCMI Metadata Terms'
		},
		{ href: 'https://www.dublincore.org/news/', label: 'News' },
		{ href: 'https://www.dublincore.org/about/contact/', label: 'Contact' }
	];
</script>

<footer class="mt-16 border-t border-stone-200 bg-stone-50" aria-labelledby="footer-heading">
	<h2 id="footer-heading" class="sr-only">Footer</h2>
	<div class="mx-auto max-w-7xl px-6 py-14 lg:px-8">
		<div class="grid gap-12 lg:grid-cols-12 lg:gap-8">
			<div class="lg:col-span-5">
				<img class="h-6 w-auto" src="/images/logos/dcpapers_v01a.svg" alt="DCPapers" />
				<p class="mt-4 max-w-sm text-sm/6 text-stone-600">
					Proceedings of the International Conference on Dublin Core and Metadata Applications.
					Published by the
					<a href={CONFIG.publisher.url} class="font-medium text-stone-700 hover:text-accent-700">
						Dublin Core Metadata Initiative</a
					>.
				</p>
				<dl class="mt-4 space-y-1 text-sm/6 text-stone-500">
					<div class="flex gap-x-2">
						<dt>ISSN</dt>
						<dd class="font-mono text-[13px]/6">
							<a
								href={getIssnUrl(CONFIG.conference.issn)}
								class="underline decoration-stone-300 underline-offset-2 hover:text-accent-700"
								target="_blank"
								rel="noopener"
							>
								{CONFIG.conference.issn}
							</a>
						</dd>
					</div>
					<div class="flex items-center gap-x-2">
						<dt class="sr-only">Access model</dt>
						<!-- Bare padlock, not the full OPEN ACCESS lockup: this is a compact metadata
						     row and the wide lockup competed with the words beside it. The transparent
						     PLoS lock is used rather than the older icon, whose white fill showed as a
						     blob on the footer's tinted background. Height-constrained because the glyph
						     is tall (640x1000); a square class would stretch it.
						     No CC button here — the fine print below already states the licence in full,
						     so a badge would say it twice. alt="" because the text beside it is the label. -->
						<dd>
							<!-- Icon and words are one link: the mark and the phrase make the same claim,
							     so splitting them would give two adjacent links to the same place. The
							     underline sits on the words only — dragging a rule under the padlock would
							     read as a stray line — and reuses the footer's existing quiet link style
							     (decoration-stone-300) rather than introducing a third treatment. -->
							<a
								href={CONFIG.license.openAccessUrl}
								class="flex items-center gap-x-2 hover:text-accent-700"
								target="_blank"
								rel="noopener"
							>
								<img
									src="/images/icons/open-access-lock_v001.svg"
									alt=""
									width="640"
									height="1000"
									class="h-4 w-auto"
								/>
								<span class="underline decoration-stone-300 underline-offset-2"
									>Diamond Open Access</span
								>
							</a>
						</dd>
					</div>
				</dl>
				<div class="mt-6 flex space-x-5">
					<SocialIcons />
				</div>
			</div>

			<nav class="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:col-span-7" aria-label="Footer">
				<div>
					<h3 class="text-sm font-semibold text-stone-900">Browse</h3>
					<ul role="list" class="mt-4 space-y-2.5">
						{#each browse as link}
							<li>
								<a href={link.href} class="text-sm/6 text-stone-600 hover:text-accent-700"
									>{link.label}</a
								>
							</li>
						{/each}
					</ul>
				</div>
				<div>
					<h3 class="text-sm font-semibold text-stone-900">Machine Access</h3>
					<ul role="list" class="mt-4 space-y-2.5">
						{#each machineAccess as link}
							<li>
								<a href={link.href} class="text-sm/6 text-stone-600 hover:text-accent-700"
									>{link.label}</a
								>
							</li>
						{/each}
					</ul>
				</div>
				<div>
					<h3 class="text-sm font-semibold text-stone-900">DCMI</h3>
					<ul role="list" class="mt-4 space-y-2.5">
						{#each dcmi as link}
							<li>
								<a href={link.href} class="text-sm/6 text-stone-600 hover:text-accent-700"
									>{link.label}</a
								>
							</li>
						{/each}
					</ul>
				</div>
			</nav>
		</div>

		<div class="mt-12 border-t border-stone-200 pt-8">
			<div class="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
				<div class="max-w-3xl space-y-2 text-xs/5 text-stone-500">
					<p>
						Unless indicated otherwise, papers are licensed under a
						<a
							class="underline decoration-stone-300 underline-offset-2 hover:text-accent-700"
							rel="license"
							href={CONFIG.license.url}
						>
							Creative Commons Attribution 4.0 International License</a
						>; metadata is dedicated to the public domain under
						<a
							class="underline decoration-stone-300 underline-offset-2 hover:text-accent-700"
							href={CONFIG.license.metadataLicense}>CC0 1.0</a
						>. See the
						<a
							class="underline decoration-stone-300 underline-offset-2 hover:text-accent-700"
							href="https://www.dublincore.org/about/copyright/#documentnotice"
							>DCMI Document Notice</a
						> for details.
					</p>
					<p>
						<a
							class="underline decoration-stone-300 underline-offset-2 hover:text-accent-700"
							href="https://www.dublincore.org/about/copyright/#copyright">Copyright</a
						>
						&copy; 1995&ndash;{year}
						<abbr title="Dublin Core Metadata Initiative"
							><a class="hover:text-accent-700" href="https://www.dublincore.org/">DCMI</a></abbr
						>. DCMI
						<a
							class="underline decoration-stone-300 underline-offset-2 hover:text-accent-700"
							href="https://www.dublincore.org/about/copyright/#liability">liability</a
						>,
						<a
							class="underline decoration-stone-300 underline-offset-2 hover:text-accent-700"
							href="https://www.dublincore.org/about/copyright/#trademark">trademark/service mark</a
						>
						and
						<a
							class="underline decoration-stone-300 underline-offset-2 hover:text-accent-700"
							href="https://www.dublincore.org/about/copyright/#documentnotice">document use</a
						>
						rules apply, together with our
						<a
							class="underline decoration-stone-300 underline-offset-2 hover:text-accent-700"
							href="https://www.dublincore.org/about/privacy/">privacy statement</a
						>. DCMI is a project of ASIS&amp;T, a U.S. 501(c)(3) nonprofit.
					</p>
				</div>
				<div class="flex shrink-0 items-center gap-x-6">
					<a href="https://www.dublincore.org/">
						<img
							class="h-7 w-auto"
							src="/images/logos/dcmi_logo_v802.svg"
							alt="Dublin Core Metadata Initiative"
						/>
					</a>
					<a href="https://www.cloudflare.com/galileo/">
						<img
							class="h-9 w-auto"
							src="https://www.dublincore.org/images/logos/galileo_logo.png"
							alt="Powered by Cloudflare Project Galileo"
							loading="lazy"
						/>
					</a>
				</div>
			</div>
		</div>
	</div>
</footer>
